import { useState, useEffect, useRef, useCallback } from "react"
import { Route, Routes, useLocation, useNavigate } from "react-router-dom"
import Homepage from "./component/Homepage/Homepage"
import Auth from "./component/Auth/Auth"
import userContext from "./context/user"
import globalToastContext from "./context/globalToast"
import Layout from "./component/Layout"
import UserList from "./component/User/List"
import UserForm from "./component/User/Form"
import RegistrationForm from "./component/Auth/Registration/RegistrationForm"
import OrderList from "./component/Order/List"
import OrderForm from "./component/Order/Form"
import OrderFormLoading from "./component/Order/Form/Loading"
import OrderFormUnloading from "./component/Order/Form/Unloading"
import OrderFormGoods from "./component/Order/Form/Goods"
import OrderFormSummary from "./component/Order/Form/Summary"
import OrderFormCalculation from "./component/Order/Form/Calculation"
import { useAuthIsAuthenticated } from "./api/queries/useAuthIsAuthenticated"
import { useAuthLogin } from "./api/queries/useAuthLogin"
import { useAuthLogout } from "./api/queries/useAuthLogout"
import ResetPassword from "./component/Auth/ResetPassword"
import SetPassword from "./component/Auth/SetPassword"
import { User } from "./api/types/user"
import UserProfile from "./component/User/Profile"
import AddressList from "./component/Address/List"
import AddressForm from "./component/Address/Form"
import { Loader } from "./component/Loader"
import { Toast } from "primereact/toast"
import { useQueryClient } from "react-query"
import { ResponseData } from "./api/types/base"
import ChangePasswordForm from "./component/User/ChangePassword"
import { Settings } from "./component/Settings"
import { Import } from "./component/Settings/Import"
import { OrderImportCSV } from "./component/Order/Import/CSV"
import AddressBookList from "./component/AddressBook/List"
import AddressBookForm from "./component/AddressBook/Form"
import OrderTemplateList from "./component/OrderTemplate/List"
import OrderTemplateForm from "./component/OrderTemplate/Form"
import { ShippingPriceForm } from "./component/ShippingPrice"

export default function App() {
  const userUpdateIntervalMs = parseInt(process.env.REACT_APP_USER_UPDATE_INTERVAL_MS as string, 10)

  const navigate = useNavigate()
  const authLogin = useAuthLogin()
  const authLogout = useAuthLogout()
  const queryClient = useQueryClient()

  const globalToastRef = useRef<Toast>(null)

  const {
    status: isAuthenticatedStatus,
    data: isAuthenticatedData,
    refetch: isAuthenticatedRefetch,
  } = useAuthIsAuthenticated()
  const { pathname } = useLocation()

  const [user, setUser] = useState<User | undefined>()
  const [loginFailed, setLoginFailed] = useState<boolean>(false)
  const [userUpdateIntervalId, setUserUpdateIntervalId] = useState<number | undefined>()
  const [appLoaded, setAppLoaded] = useState<boolean>(false)

  const login = async (username: string, password: string): Promise<void> => {
    const data = await authLogin.mutateAsync({ username, password })
    setUser(data?.results.userDetail)
    setLoginFailed(false)
    navigate("/")
  }

  const logout = useCallback(async () => {
    await authLogout.mutateAsync()
    setLoginFailed(false)
    navigate("/login")
  }, [authLogout, navigate])

  const hasRole = (): boolean => false
  const hasPerm = (): boolean => false

  const isPublicURL = useCallback(() => {
    return pathname === "/login" ||
      pathname === "/registration" ||
      pathname === "/reset-password" ||
      pathname.startsWith("/set-password")
  }, [pathname])

  useEffect(() => {
    if (isAuthenticatedStatus === "error" && !user && !isPublicURL()) {
      clearInterval(userUpdateIntervalId)
      setUserUpdateIntervalId(undefined)
      navigate("/login")
    }
  }, [isAuthenticatedStatus, user, pathname, navigate, isPublicURL, userUpdateIntervalId])

  useEffect(() => {
    if (!user && isAuthenticatedData) {
      setUser(isAuthenticatedData.results.userDetail)
    }
  }, [isAuthenticatedData, user])

  // update user periodicaly
  useEffect(() => {
    if (isPublicURL()) {
      clearInterval(userUpdateIntervalId)
      setUserUpdateIntervalId(undefined)
    } else {
      if (!userUpdateIntervalId) {
        const id = setInterval(async () => {
          const { data } = await isAuthenticatedRefetch()
          setUser(data?.results.userDetail)
        }, userUpdateIntervalMs, [])

        setUserUpdateIntervalId(id)
      }
    }
  }, [isAuthenticatedRefetch, userUpdateIntervalMs, isPublicURL, userUpdateIntervalId])

  useEffect(() => {
    const queryCache = queryClient.getQueryCache()
    const mutationCache = queryClient.getMutationCache()

    const onError = async (err: any) => {
      const { response } = err

      if (!response) {
        globalToastRef.current?.show({
          severity: "error",
          summary: "Nepodařilo se načíst data ze serveru.",
        })
        return
      }

      const { data, status }: { data: ResponseData, status: number } = response
      const { url } = response.config

      if (status === 422 &&
        (
          url.startsWith("/loadings/terms") ||
          url.startsWith("/unloadings/terms") ||
          url.startsWith("/unloadings/times")
        )
      ) {
        console.warn("Skipped message for:", url)
        return
      }

      if (status === 401 && !isPublicURL()) {
        await logout()
      }

      if (url === "/auth") {
        setLoginFailed(true)
      }

      if (data.messages.length > 0) {
        const messages = data.messages.map((message) => ({
          severity: message.type,
          summary: message.message || "Neznámá chyba.",
        }))

        globalToastRef.current?.show(messages)
      }

      if (data.messages.length === 0 && data.errors.length === 0) {
        if (url !== "/is-authenticated") {
          globalToastRef.current?.show({
            severity: "error",
            summary: "Neznámá chyba.",
          })
        }
      }
    }

    queryCache.config.onError = onError
    mutationCache.config.onError = onError
  }, [queryClient, logout, isPublicURL])

  useEffect(() => {
    if (
      pathname === "/login" ||
      pathname === "/reset-password" ||
      pathname.startsWith("/set-password")
    ) {
      document.body.classList.add("gradient")
    } else {
      document.body.classList.remove("gradient")
    }
  }, [pathname])

  useEffect(() => {
    if (isAuthenticatedStatus !== "loading") {
      setAppLoaded(true)
    }
  }, [isAuthenticatedStatus])

  if (!appLoaded) {
    return <Loader isVisible={true} />
  }

  return (
    <userContext.Provider value={{ user, setUser, login, logout, hasRole, hasPerm }}>
      <globalToastContext.Provider value={{ ref: globalToastRef }}>
        <Toast ref={globalToastRef} />
        <Loader isVisible={isAuthenticatedStatus === "loading"} />

        <Routes>
          <Route path={"/login"} element={<Auth loginFailed={loginFailed} />} />
          <Route path={"/reset-password"} element={<ResetPassword />} />
          <Route path={"/set-password/:token"} element={<SetPassword />} />
          <Route path={"/registration"} element={<RegistrationForm />} />
          <Route path={"/"} element={<Layout />}>
            <Route path={"profile"} element={<UserProfile />} />
            <Route path={"profile/change-password"} element={<ChangePasswordForm />} />
            <Route path={"users"} element={<UserList />} />
            <Route path={"users/new"} element={<UserForm />} />
            <Route path={"users/:uuid"} element={<UserForm />} />
            <Route path={"addresses"} element={<AddressList />} />
            <Route path={"addresses/new"} element={<AddressForm />} />
            <Route path={"addresses/:uuid"} element={<AddressForm />} />
            <Route path={"address-book"} element={<AddressBookList />} />
            <Route path={"address-book/new"} element={<AddressBookForm />} />
            <Route path={"address-book/:uuid"} element={<AddressBookForm />} />
            <Route path={"orders"} element={<OrderList />} />
            <Route path={"orders/import/csv"} element={<OrderImportCSV />} />
            <Route path={"orders/:uuid"} element={<OrderForm />} >
              <Route path={"loading"} element={<OrderFormLoading />} />
              <Route path={"unloading"} element={<OrderFormUnloading />} />
              <Route path={"goods"} element={<OrderFormGoods />} />
              <Route path={"calculation"} element={<OrderFormCalculation />} />
              <Route path={"summary"} element={<OrderFormSummary />} />
            </Route>
            <Route path={"settings"} element={<Settings />}>
              <Route path={"import"} element={<Import />} />
            </Route>
            <Route path={"order-templates"} element={<OrderTemplateList />} />
            <Route path={"order-templates/new"} element={<OrderTemplateForm />} />
            <Route path={"order-templates/:uuid"} element={<OrderTemplateForm />} />
            <Route path={"shipping-price"} element={<ShippingPriceForm />} />
            <Route path={""} element={<Homepage />} />
          </Route>
        </Routes>
      </globalToastContext.Provider>
    </userContext.Provider>
  )
}
